import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronDown, LayoutDashboard } from "lucide-react";
import {
  getLaboralFlujoPasoForPath,
  getLaboralFlujoSection,
  getLaboralFlujoStepperItems,
} from "@/lib/laboralFlujoOperativo";
import {
  LABORAL_FLUJO_HUB_RESET_EVENT,
  LABORAL_FLUJO_OPEN_STATE_KEY,
  consumeLaboralFlujoOpenFlag,
  markLaboralFlujoOpenOnArrival,
} from "@/lib/laboralFlujoNav";
import { resolveModuleNavIcon } from "@/modules/moduleNavIcons";
import { useWorkModulePanel } from "@/lib/workModuleContext";
import { WORK_MODULES, getStoredWorkModule } from "@/modules/workModuleCore";
import { isDatosMaestrosNavigationActive } from "@/modules/datosMaestros/datosMaestrosNavigation";
import ClientWorkPickerDialog from "@/components/ClientWorkPickerDialog";
import api from "@/lib/api";
import { CLIENT_MODULE_SCOPES } from "@/lib/clientModuleScope";
import { getStoredClientId, setStoredClientId } from "@/lib/clientSelection";

/** Rutas del flujo laboral que no exigen cliente al Abrir. */
const LABORAL_PATHS_SKIP_CLIENT_GATE = new Set([
  "/clientes",
  "/datos-maestros",
  "/ayuda",
  "/gestion-datos",
  "/laboral/inicio",
]);

function laboralPathNeedsClient(path) {
  const base = String(path || "").split("?")[0];
  if (!base || LABORAL_PATHS_SKIP_CLIENT_GATE.has(base)) return false;
  return true;
}

function resolveCtaLabel(item, { active, expanded, hasContent, focusMode }) {
  if (focusMode) return "Ocultar";
  if (active && hasContent) {
    if (expanded) return "Ocultar";
    return item.step === 6 ? "Ver alertas" : "Desplegar";
  }
  if (active) return "Está aquí";
  return "Abrir";
}

function FlujoStepCard({
  item,
  active,
  done,
  expanded,
  hasContent,
  focusMode = false,
  onCardClick,
  innerContent = null,
}) {
  const Icon = resolveModuleNavIcon(item.iconKey, LayoutDashboard);
  const ctaLabel = resolveCtaLabel(item, { active, expanded, hasContent, focusMode });
  const CtaIcon = (active && hasContent && expanded) || focusMode ? ChevronDown : ArrowRight;
  const showInner = Boolean((expanded || focusMode) && hasContent && innerContent);

  const cardClass = [
    "group relative flex flex-col rounded-sm border bg-white transition-shadow w-full text-left overflow-hidden",
    showInner || focusMode ? "shadow-md" : "min-h-[148px] hover:shadow-md",
    "focus-within:ring-2 focus-within:ring-[#3DA873]/50",
    active
      ? showInner || focusMode
        ? "border-[#3DA873] ring-2 ring-[#3DA873]/50"
        : "border-[#3DA873] ring-1 ring-[#3DA873]/40 shadow-sm"
      : done
        ? "border-[#3DA873]/30 hover:border-[#3DA873]/45"
        : "border-slate-200 hover:border-[#0F2D5C]/20",
  ].join(" ");

  return (
    <article className={cardClass} data-testid={`laboral-flujo-paso-${item.step}`}>
      <button
        type="button"
        onClick={() => onCardClick(item)}
        className="flex flex-col flex-1 w-full p-4 text-left outline-none"
        title={item.hint}
        aria-expanded={active && hasContent ? expanded || focusMode : undefined}
      >
        <div className="flex items-start gap-3 mb-3 flex-1">
          <div
            className={`p-2 rounded-sm shrink-0 ${
              active ? "bg-[#3DA873]/15 text-[#0F2D5C]" : "bg-[#0F2D5C]/10 text-[#0F2D5C]"
            }`}
          >
            <Icon className="w-4 h-4" strokeWidth={1.75} aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <span className="inline-block text-[9px] font-bold uppercase tracking-wider text-[#3DA873] mb-1">
              Paso {item.step}
              {done ? (
                <span className="ml-1.5 font-semibold normal-case tracking-normal text-[#3DA873]/80">
                  · Completado
                </span>
              ) : null}
            </span>
            <h3 className="text-sm font-semibold text-[#0F2D5C] leading-snug">{item.label}</h3>
            {item.hint ? (
              <p
                className={`text-[11px] text-slate-600 mt-1.5 leading-snug ${
                  showInner || focusMode ? "" : "line-clamp-3"
                }`}
              >
                {item.hint}
              </p>
            ) : null}
          </div>
        </div>
        <span
          className={`inline-flex items-center justify-center w-full rounded-sm h-9 text-xs font-semibold mt-auto ${
            active
              ? "bg-[#3DA873] text-white"
              : "bg-[#0F2D5C] text-white group-hover:bg-[#0a2248]"
          }`}
        >
          {ctaLabel}
          <CtaIcon
            className={`w-3.5 h-3.5 ml-1.5 ${
              (active && hasContent && expanded) || focusMode ? "rotate-180" : ""
            }`}
            aria-hidden
          />
        </span>
      </button>
      {showInner ? (
        <div
          className="border-t border-slate-100 px-4 sm:px-5 pb-5 pt-4 space-y-6 bg-slate-50/40"
          data-testid="laboral-flujo-step-content"
        >
          {innerContent}
        </div>
      ) : null}
    </article>
  );
}

/**
 * Flujo operativo laboral — hub de tarjetas (como Visor) y modo foco con una sola tarjeta abierta.
 * Pasa children para el panel de trabajo del paso activo en la ruta actual.
 */
export default function LaboralFlujoOperativo({ compact = false, children = null }) {
  const location = useLocation();
  const { pathname, search, state } = location;
  const navigate = useNavigate();
  const clientScope = CLIENT_MODULE_SCOPES.ASISTENTE_LABORAL;
  const [openStep, setOpenStep] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(() => getStoredClientId(clientScope));
  const [clientGate, setClientGate] = useState({ open: false, path: "", label: "", step: null });
  const activo = getLaboralFlujoPasoForPath(pathname, search);
  const section = activo ? getLaboralFlujoSection(activo.sectionId) : null;
  const stepper = getLaboralFlujoStepperItems();
  const hasContent = Boolean(children);
  const activeStep = activo?.step;

  useEffect(() => {
    const stateStep = state?.[LABORAL_FLUJO_OPEN_STATE_KEY];
    if (stateStep != null && activeStep != null) {
      setOpenStep(Number(stateStep));
      return;
    }
    if (consumeLaboralFlujoOpenFlag(activeStep)) {
      setOpenStep(activeStep);
      return;
    }
    setOpenStep(null);
  }, [pathname, search, state?.[LABORAL_FLUJO_OPEN_STATE_KEY], activeStep]);

  useEffect(() => {
    const onHubReset = () => setOpenStep(null);
    window.addEventListener(LABORAL_FLUJO_HUB_RESET_EVENT, onHubReset);
    return () => window.removeEventListener(LABORAL_FLUJO_HUB_RESET_EVENT, onHubReset);
  }, []);

  useEffect(() => {
    api.get("/clients")
      .then(({ data }) => setClients(Array.isArray(data) ? data : []))
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    setClientId(getStoredClientId(clientScope));
  }, [clientScope]);

  useEffect(() => {
    const sync = () => setClientId(getStoredClientId(clientScope));
    const onChanged = (e) => {
      if (e?.detail?.scope && e.detail.scope !== clientScope) return;
      setClientId(e?.detail?.clientId ?? getStoredClientId(clientScope));
    };
    window.addEventListener("storage", sync);
    window.addEventListener("visor:client-changed", onChanged);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("visor:client-changed", onChanged);
    };
  }, [clientScope]);

  const isActiveStep = (item) => {
    if (activo?.group && item.paths?.length > 1) {
      return item.paths.some((p) => {
        const base = p.split("?")[0];
        if (base === pathname && p.includes("tab=")) {
          return p.includes(`tab=${activo.tramitesTab}`);
        }
        return p.split("?")[0] === pathname;
      });
    }
    if (activo && item.paths?.includes(activo.path)) return true;
    return item.paths?.some((p) => p.split("?")[0] === pathname);
  };

  const activeItem = useMemo(
    () => stepper.find((item) => isActiveStep(item)) || null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pathname/search/activo derivan del flujo
    [stepper, pathname, search, activo],
  );

  const goToStep = (item, targetPath) => {
    markLaboralFlujoOpenOnArrival(item.step);
    navigate(targetPath, { state: { [LABORAL_FLUJO_OPEN_STATE_KEY]: item.step } });
  };

  const navigateToStep = (item) => {
    const targetPath = item.paths?.[0] || "/datos-maestros";
    const needsClient = laboralPathNeedsClient(targetPath);
    const hasClient = Boolean(String(clientId || "").trim());
    if (needsClient && !hasClient) {
      setClientGate({
        open: true,
        path: targetPath,
        label: item.label || "",
        step: item.step,
      });
      return;
    }
    goToStep(item, targetPath);
  };

  const handleClientSelect = (id) => {
    setStoredClientId(id, clientScope);
    setClientId(id);
    const pendingPath = clientGate.path;
    const pendingStep = clientGate.step;
    const pendingLabel = clientGate.label;
    setClientGate({ open: false, path: "", label: "", step: null });
    if (!pendingPath) return;
    const item = stepper.find((s) => s.step === pendingStep)
      || { step: pendingStep, label: pendingLabel, paths: [pendingPath] };
    goToStep(item, pendingPath);
  };

  const collapseToHub = () => setOpenStep(null);

  const handleCardClick = (item) => {
    const active = isActiveStep(item);
    if (active && hasContent) {
      if (openStep === item.step) {
        collapseToHub();
      } else {
        setOpenStep(item.step);
      }
      return;
    }
    if (active) return;
    navigateToStep(item);
  };

  if (compact) {
    return (
      <div
        className="rounded-md border border-[#0F2D5C]/15 bg-[#0F2D5C]/[0.04] px-3 py-2 text-xs text-[#0F2D5C]/90"
        data-testid="laboral-flujo-compact"
      >
        {activo ? (
          <span>
            Paso <strong>{activo.step}</strong> · {activo.label}
            {section ? ` — ${section.title}` : ""}
          </span>
        ) : (
          <span>Flujo operativo: inicio → datos maestros → patronales → catálogo → REPSE → reportes IMSS</span>
        )}
      </div>
    );
  }

  const inFocusMode =
    hasContent && openStep != null && openStep === activeStep && activeItem != null;

  return (
    <section
      className="px-8 max-w-6xl"
      data-testid="laboral-flujo-operativo"
      aria-label="Orden operativo recomendado"
    >
      <div
        className="rounded-lg border border-[#0F2D5C]/15 bg-white shadow-sm overflow-hidden"
        data-testid={inFocusMode ? "laboral-flujo-focus" : "laboral-flujo-hub"}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#0F2D5C]/10 bg-[#0F2D5C] px-4 py-2.5 text-white">
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] text-white/60">Orden operativo recomendado</p>
            <p className="text-sm font-medium">
              {section ? section.title : "Asistente Laboral y REPSE"}
            </p>
          </div>
          {activo ? (
            <div className="text-right text-xs text-white/80 max-w-md">
              <span className="font-semibold text-[#3DA873]">Paso {activo.step}</span>
              {" · "}
              {activo.label}
            </div>
          ) : (
            <p className="text-xs text-white/70 max-w-sm text-right">
              Siga los pasos en orden para no omitir datos del cliente ni del IMSS.
            </p>
          )}
        </div>

        <div className="p-4 sm:p-5">
          {inFocusMode ? (
            <>
              <button
                type="button"
                onClick={collapseToHub}
                className="mb-4 inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 hover:border-slate-300 transition-colors"
                data-testid="laboral-flujo-ver-todos-pasos"
              >
                <ArrowLeft className="w-4 h-4 shrink-0" strokeWidth={2.25} aria-hidden />
                Ver todos los pasos
              </button>
              <FlujoStepCard
                item={activeItem}
                active
                done={false}
                expanded
                focusMode
                hasContent={hasContent}
                onCardClick={handleCardClick}
                innerContent={children}
              />
            </>
          ) : (
            <ol className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 list-none m-0 p-0">
              {stepper.map((item) => {
                const active = isActiveStep(item);
                const done = activo && item.step < activo.step;

                return (
                  <li key={`${item.step}-${item.shortLabel}`} className="min-w-0">
                    <FlujoStepCard
                      item={item}
                      active={active}
                      done={done}
                      expanded={false}
                      hasContent={hasContent}
                      onCardClick={handleCardClick}
                    />
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>

      <ClientWorkPickerDialog
        open={clientGate.open}
        onOpenChange={(open) => {
          if (!open) setClientGate({ open: false, path: "", label: "", step: null });
        }}
        clients={clients}
        clientId={clientId}
        onSelect={handleClientSelect}
        mode="require"
        pendingLabel={clientGate.label}
      />
    </section>
  );
}

/** Envuelve children con el flujo solo en módulo Asistente Laboral (p. ej. /clientes compartido). */
export function LaboralFlujoMaybe({ children }) {
  const location = useLocation();
  const panel = useWorkModulePanel();
  if (isDatosMaestrosNavigationActive(location)) return children;

  const isLaboral =
    panel?.activeModule === WORK_MODULES.ASISTENTE_LABORAL
    || getStoredWorkModule() === WORK_MODULES.ASISTENTE_LABORAL;
  if (!isLaboral) return children;
  return <LaboralFlujoOperativo>{children}</LaboralFlujoOperativo>;
}
